import { Component, OnInit, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { LoggingService } from '../../core/services/logging.service';
import { AuthService } from '../../core/services/auth.service';
import { CreateRequestModalService } from '../../core/services/create-request-modal.service';

/**
 * External login component - handles Salesforce SAML redirect.
 *
 * Expected flow (secure POST from external portal):
 * - External portal POSTs a form to /login/external with:
 *   - SAMLResponse in POST body (form field) or sessionStorage.pendingSamlResponse
 *   - Redirect destination in POST body (form field) or sessionStorage.redirectPath
 * - Component extracts both from sessionStorage (populated by browser form POST)
 * - Component sends SAMLResponse to BFF in custom header (x-saml-response)
 * - BFF validates with API and creates session
 * - Component redirects to specified path or home route
 *
 * Error flow:
 * - BFF validation failed (invalid SAML, user not found, inactive, etc.)
 * - Component captures error and redirects to login page
 */
@Component({
  selector: 'app-external-login',
  standalone: true,
  imports: [],
  template: `
    <div class="external-login-container">
      <div class="loading-spinner">
        <h2>Completing external authentication...</h2>
        <p>Redirecting...</p>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        font-size: 14px;
        font-weight: 500;
        color: #1a1a1a;
        font-family: var(
          --ukg_sys_fontFamily_primaryTight,
          -apple-system,
          BlinkMacSystemFont,
          'Segoe UI',
          Roboto,
          sans-serif
        );
      }
      .external-login-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100vh;
        background-color: #f5f5f5;
      }
      .loading-spinner {
        text-align: center;
        padding: 2rem;
        background: #f5f5f5;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    `,
  ],
})
export class ExternalLoginComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly logger = inject(LoggingService);
  private readonly authService = inject(AuthService);
  private readonly createRequestModalService = inject(
    CreateRequestModalService
  );

  // Allowed redirect paths for security (whitelist)
  private readonly allowedRedirectPaths = ['/requests', '/requests/create'];

  private isValidRedirectPath(path: string | null): boolean {
    if (!path) return false;
    return this.allowedRedirectPaths.some(allowed => allowed === path);
  }

  ngOnInit(): void {
    // Extract SAMLResponse from query parameters (for testing) or sessionStorage (for form POST)
    let samlResponse = this.route.snapshot.queryParamMap.get('SAMLResponse');

    // If not in query params, check if it was sent via form POST (stored in sessionStorage by browser)
    if (!samlResponse && typeof sessionStorage !== 'undefined') {
      samlResponse = sessionStorage.getItem('pendingSamlResponse');
      if (samlResponse) {
        sessionStorage.removeItem('pendingSamlResponse');
      }
    }

    // Extract redirect destination from query parameters (for testing) or sessionStorage (for form POST)
    let redirectParam = this.route.snapshot.queryParamMap.get('redirect');

    // If not in query params, check if it was sent via form POST (stored in sessionStorage)
    if (!redirectParam && typeof sessionStorage !== 'undefined') {
      redirectParam = sessionStorage.getItem('redirectPath');
      if (redirectParam) {
        sessionStorage.removeItem('redirectPath');
      }
    }

    // Check for error query parameter from BFF
    const error = this.route.snapshot.queryParamMap.get('error');

    if (error) {
      // BFF validation failed - redirect to login with error message
      this.logger.error('External auth received error from BFF: ' + error);
      this.authService.setAuthError(error);
      this.router.navigate(['/login']);
      return;
    }

    if (!samlResponse) {
      // No SAML response provided - invalid request
      this.logger.error('External auth: Missing SAMLResponse parameter');
      this.authService.setAuthError('VALIDATION_FAILED');
      this.router.navigate(['/login']);
      return;
    }

    // Send SAML response to BFF for validation
    this.logger.info('External auth: Processing SAML response');
    this.authService.externalLogin(samlResponse).subscribe({
      next: user => {
        if (user) {
          // Session created successfully, determine redirect destination
          let destination = this.isValidRedirectPath(redirectParam)
            ? redirectParam!
            : this.authService.getHomeRoute();

          // If redirect is to create request modal, navigate to requests and signal modal to open
          const openCreateModal = destination === '/requests/create';
          if (openCreateModal) {
            destination = '/requests';
          }
          this.logger.info(
            `External auth: Session created, redirecting to ${destination}`
          );
          void this.router.navigate([destination]).then(() => {
            // Emit signal to open modal after navigation completes
            if (openCreateModal) {
              // Small delay to ensure component subscription is initialized
              setTimeout(() => {
                this.createRequestModalService.triggerOpenModal();
              }, 100);
            }
          });
        } else {
          // Validation failed but no specific error code was set
          this.logger.error('External auth: SAML validation failed');
          this.authService.setAuthError('VALIDATION_FAILED');
          this.router.navigate(['/login']);
        }
      },
      error: err => {
        // BFF error (network, 401, 403, 500, etc.)
        this.logger.error('External auth: BFF returned error', err);
        this.authService.setAuthError('VALIDATION_FAILED');
        this.router.navigate(['/login']);
      },
    });
  }
}
